import { getCurrencySymbol, NgIf } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { RootState } from '@next/core-lib/app';
import { AbstractDialog, DialogComponent } from '@next/core-lib/dialog';
import type { AbstractDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import {
  Calculation,
  QuickSimulation,
  QuickSimulationTableRow,
} from '../../../models/feed-profit.model';
import * as actions from '../../../store/feed-profit.actions';
import { getNextQuickSimulationId } from '../../../store/feed-profit.selectors';

export interface AddQuickSimulationDialogInputtData extends AbstractDialogData {
  calculation: Calculation;
  simulation?: QuickSimulationTableRow;
}

export interface AddQuickSimulationResultData {
  simulation: QuickSimulation;
  calculation: Calculation;
}

@Component({
  selector: 'app-quick-simulation-item-dialog',
  templateUrl: './quick-simulation-item.component.html',
  styleUrls: ['./quick-simulation-item.component.scss'],
  imports: [
    DialogComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    AgrNumberDirective,
    NgIf,
    MatError,
    MatPrefix,
    TranslatePipe,
  ],
})
export class QuickSimulationItemComponent extends AbstractDialog<AddQuickSimulationResultData> {
  calculation: Calculation;
  currencySymbol: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddQuickSimulationDialogInputtData,
    protected mdDialogRef: MatDialogRef<
      QuickSimulationItemComponent,
      AbstractDialogResultData<AddQuickSimulationResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.currencySymbol = this.currencySymbol = getCurrencySymbol(
      this.localeService.getCustomerCurrency(),
      'narrow',
    );
    this.calculation = data.calculation;
  }

  formGroup = new FormGroup({
    startWeight: new FormControl(this.data.simulation?.startWeight, {
      validators: [Validators.required],
    }),
    pigletPrice: new FormControl(this.data.simulation?.pigletPrice, {
      validators: [Validators.required],
    }),
    slaughteredWeightInKg: new FormControl(this.data.simulation?.slaughteredWeightInKg, {
      validators: [Validators.required],
    }),
    revenuePerKg: new FormControl(this.data.simulation?.revenuePerKg, {
      validators: [Validators.required],
    }),
    feedPackagePrice: new FormControl(this.data.simulation?.feedPacketPrice, {
      validators: [Validators.required],
    }),
  });
  nextQuickSimulationId$: Observable<number> = this.store.select(getNextQuickSimulationId);

  save() {
    if (this.formGroup.invalid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const { startWeight, pigletPrice, slaughteredWeightInKg, revenuePerKg, feedPackagePrice } =
      this.formGroup.value;

    this.confirm({
      calculation: this.calculation,
      simulation: {
        id: this.data.simulation?.id
          ? this.data.simulation?.id
          : this.getNextSimId(this.calculation),
        startWeight,
        pigletPrice,
        slaughteredWeightInKg,
        revenuePerKg,
        feedPackagePrice,
        calculatedFeedPackagePrice: 0,
      },
    });
  }

  //save and create new
  alt() {
    if (this.formGroup.invalid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const { startWeight, pigletPrice, slaughteredWeightInKg, revenuePerKg, feedPackagePrice } =
      this.formGroup.value;
    const newSimulation = {
      id: this.getNextSimId(this.calculation),
      startWeight,
      pigletPrice,
      slaughteredWeightInKg,
      revenuePerKg,
      feedPackagePrice,
    };

    const newCalculation = <Calculation>{
      ...this.calculation,
      quickSimulations: <QuickSimulation[]>[...this.calculation.quickSimulations, newSimulation],
    };
    this.store.dispatch(
      actions.upsertCalculationRequest({
        calculation: newCalculation,
      }),
    );

    this.calculation = newCalculation;
    this.formGroup.reset();
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  getNextSimId(calculation: Calculation) {
    if (calculation && calculation.comparisonSimulation && calculation.quickSimulations) {
      let biggestId = 0;
      [calculation.comparisonSimulation, ...calculation.quickSimulations].forEach((d) => {
        if (d.id > biggestId) {
          biggestId = d.id;
        }
      });
      return biggestId + 1;
    }
    return 1;
  }
}
